import { Link } from 'react-router-dom';
import type { AllianceMemberView, AllianceRole } from '@kaeliux/shared';
import { ALLIANCE_ROLE_LABELS } from '@kaeliux/shared';
import { canManageMember } from '@kaeliux/game-engine';
import { cn } from '../utils/cn';
import { formatFullNumber } from '../utils/formatters';
import { RACE_LOGOS } from '../utils/raceAssets';
import { Table, Th, Td, TABLE_ROW_HIGHLIGHT_CLASSES, TABLE_ROW_BORDER_CLASSES } from './ui/Table';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import AllianceLeaderIcon from './AllianceLeaderIcon';

interface AllianceMemberTableProps {
    members: AllianceMemberView[];
    viewerId: string;
    viewerRole: AllianceRole | null;
    onKick: (member: AllianceMemberView) => void;
    onSetRole: (member: AllianceMemberView, role: 'officer' | 'member') => void;
    onTransferLeadership: (member: AllianceMemberView) => void;
    busyMemberId: string | null;
}

/**
 * Roster with per-row management actions. Which buttons render is decided by
 * the SAME `canManageMember` the server enforces — the UI never invents its
 * own permission rule, it just hides what would be rejected anyway.
 */
export default function AllianceMemberTable({
    members,
    viewerId,
    viewerRole,
    onKick,
    onSetRole,
    onTransferLeadership,
    busyMemberId,
}: AllianceMemberTableProps) {
    // Contribution points are internal to the alliance — the server sends
    // them to its own members only, so their presence decides the column.
    const showContribution = members.some((m) => m.contributionPoints !== undefined);
    return (
        <Table stickyHeader data-testid="alliance-members">
            <thead>
                <tr>
                    <Th sticky>Member</Th>
                    <Th sticky>Role</Th>
                    <Th sticky>Points</Th>
                    {showContribution && <Th sticky>Contribution</Th>}
                    <Th sticky>Joined</Th>
                    <Th sticky srOnly>Actions</Th>
                </tr>
            </thead>
            <tbody>
                {members.map((member) => {
                        const actor = viewerRole ? { userId: viewerId, role: viewerRole } : null;
                        const target = { userId: member.userId, role: member.role };
                        const canKick = actor ? canManageMember(actor, target, 'kickMembers').allowed : false;
                        const canSetRole = actor ? canManageMember(actor, target, 'manageRoles').allowed : false;
                        const canTransfer = actor
                            ? canManageMember(actor, target, 'transferLeadership').allowed
                            : false;
                        const busy = busyMemberId === member.userId;

                        return (
                            <tr
                                key={member.userId}
                                className={cn(
                                    TABLE_ROW_BORDER_CLASSES,
                                    member.userId === viewerId && TABLE_ROW_HIGHLIGHT_CLASSES,
                                )}
                                data-testid={`alliance-member-${member.displayName}`}
                            >
                                <Td>
                                    <Link
                                        to={`/player/${member.userId}`}
                                        className="inline-flex items-center gap-2 hover:text-content-primary"
                                    >
                                        <img
                                            src={RACE_LOGOS[member.race]}
                                            alt=""
                                            className="w-7 h-7 rounded-full object-cover shrink-0"
                                        />
                                        <span className="text-content-primary">{member.displayName}</span>
                                        {member.role === 'leader' && <AllianceLeaderIcon />}
                                    </Link>
                                </Td>
                                <Td>
                                    <Badge
                                        tone={
                                            member.role === 'leader'
                                                ? 'accent'
                                                : member.role === 'officer'
                                                  ? 'info'
                                                  : 'neutral'
                                        }
                                    >
                                        {ALLIANCE_ROLE_LABELS[member.role]}
                                    </Badge>
                                </Td>
                                <Td className="text-content-primary">{formatFullNumber(member.points)}</Td>
                                {showContribution && (
                                    <Td className="text-content-secondary" data-testid="alliance-member-contribution">
                                        {formatFullNumber(member.contributionPoints ?? 0)}
                                    </Td>
                                )}
                                <Td className="text-content-muted">
                                    {member.joinedAt.toLocaleDateString()}
                                </Td>
                                <Td align="right">
                                    <div className="flex gap-2 justify-end flex-wrap">
                                        {canSetRole && member.role === 'member' && (
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                size="sm"
                                                loading={busy}
                                                onClick={() => onSetRole(member, 'officer')}
                                            >
                                                Promote
                                            </Button>
                                        )}
                                        {canSetRole && member.role === 'officer' && (
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                size="sm"
                                                loading={busy}
                                                onClick={() => onSetRole(member, 'member')}
                                            >
                                                Demote
                                            </Button>
                                        )}
                                        {canTransfer && (
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                size="sm"
                                                loading={busy}
                                                onClick={() => onTransferLeadership(member)}
                                            >
                                                Make leader
                                            </Button>
                                        )}
                                        {canKick && (
                                            <Button
                                                type="button"
                                                variant="danger"
                                                size="sm"
                                                loading={busy}
                                                onClick={() => onKick(member)}
                                            >
                                                Kick
                                            </Button>
                                        )}
                                    </div>
                                </Td>
                            </tr>
                        );
                })}
            </tbody>
        </Table>
    );
}
